import type { z } from 'zod';

// One place turns a schema's complaint into a sentence about the field the panel shows, for
// every step type and connector action alike: `Fill in “URL”`, not "input.url: Invalid input".

export interface IFieldLabelled {
  path: string;
  label: string;
}

type TIssue = z.core.$ZodIssue;

const KIND: Record<string, string> = {
  number: 'a number',
  int: 'a whole number',
  string: 'text',
  boolean: 'on or off',
  array: 'a list',
  object: 'a set of names and values',
};

const FORMAT: Record<string, string> = {
  url: 'a full address, like https://api.example.com/orders',
  email: 'an email address, like ops@example.com',
  uuid: 'an id',
};

/** The label of the most specific field whose `path` (with `*` for any item) covers `field`. */
export function fieldLabel(fields: readonly IFieldLabelled[], field: string): string | null {
  if (field === '') return null;
  const parts = field.split('.');
  let best: IFieldLabelled | null = null;
  for (const candidate of fields) {
    const pattern = candidate.path.split('.');
    if (pattern.length > parts.length) continue;
    if (!pattern.every((segment, at) => segment === '*' || segment === parts[at])) continue;
    if (!best || pattern.length > best.path.split('.').length) best = candidate;
  }
  return best?.label ?? null;
}

export function fallbackLabel(field: string): string {
  const last = field
    .split('.')
    .filter((part) => part !== '' && !/^\d+$/.test(part))
    .pop();
  return last ? last.replace(/([a-z])([A-Z])/g, '$1 $2').toLowerCase() : 'the settings';
}

const isEmpty = (value: unknown): boolean =>
  value === undefined ||
  value === null ||
  (typeof value === 'string' && value.trim() === '') ||
  (Array.isArray(value) && value.length === 0) ||
  (typeof value === 'object' && !Array.isArray(value) && Object.keys(value).length === 0);

const capital = (text: string): string => text.charAt(0).toUpperCase() + text.slice(1);

export function plainProblem(issue: TIssue, value: unknown, label: string): string {
  const fill = `Fill in “${label}”`;
  const empty = isEmpty(value);
  switch (issue.code) {
    case 'invalid_type':
      return empty ? fill : `“${label}” must be ${KIND[issue.expected] ?? issue.expected}`;
    case 'too_small': {
      const min = Number(issue.minimum);
      if (empty) return fill;
      if (issue.origin === 'string') return `“${label}” needs at least ${min} characters`;
      if (issue.origin === 'array' || issue.origin === 'set')
        return `“${label}” needs at least ${min}`;
      return `“${label}” must be at least ${min}`;
    }
    case 'too_big': {
      const max = Number(issue.maximum);
      if (issue.origin === 'string') return `“${label}” is too long — at most ${max} characters`;
      if (issue.origin === 'array' || issue.origin === 'set')
        return `“${label}” holds at most ${max}`;
      return `“${label}” must be at most ${max}`;
    }
    case 'invalid_format':
      // A pattern's own words ("lowercase letters, digits and _") say more than "the expected form".
      if (issue.format === 'regex' && !issue.message.startsWith('Invalid')) {
        return empty ? fill : `“${label}”: ${issue.message}`;
      }
      return `“${label}” must be ${FORMAT[issue.format] ?? 'in the expected form'}`;
    case 'invalid_value':
      return empty
        ? `Choose “${label}”`
        : `“${label}”: choose one of ${issue.values.map((option) => String(option)).join(', ')}`;
    case 'invalid_union':
      return empty ? fill : `“${label}” is not in a form this step accepts`;
    case 'unrecognized_keys':
      return `“${label}”: ${issue.keys.join(', ')} is not a setting here`;
    case 'not_multiple_of':
      return `“${label}” must be a multiple of ${String(issue.divisor)}`;
    default:
      return capital(issue.message);
  }
}
